import type { NodeEntry } from 'slate';
import { Editor, Path } from 'slate';

import { resolveParagraphEntryByLocationAnchor } from './resolve-location-anchor';
import { stampReasoningOnFirstSourcedEdit } from './suggestion-reasoning';
import { serializeRemovedColumnCellsToPreviewHtml } from '../../../serializers/html';
import type { TableCellElement, TableElement } from '../../internal';
import {
  AlineTransforms,
  EditingMode,
  isTableCellElement,
  isTableElement,
  Queries,
} from '../../internal';
import type { SuggestionSource } from '../../types';

export interface ApplyRemoveColumnsOutputOptions {
  /**
   * The author ID for the suggested edits (typically the AI or system user)
   */
  authorId: string;
  /**
   * Identifies the whole column removal as one group, so accept/reject acts
   * on it as a unit
   */
  groupId: string;
  /**
   * The locationIds of paragraphs inside cells of the columns to remove — one
   * contiguous run of columns in a single table (the shape
   * `partitionRemoveColumnAnchorsIntoRuns` produces). Any paragraph inside
   * any cell of a column identifies it.
   */
  locationAnchors: string[];
  /**
   * Why the columns are being removed — stored on the first sourced edit of
   * the removal
   */
  reasoning: string;
  /**
   * The source stamp carried by every marked edit — or undefined to
   * materialize the removal as the author's own tracked changes, with no
   * source stamps and no stored reasoning (no AI chrome).
   */
  source: SuggestionSource | undefined;
}

/**
 * Applies a `remove-columns` structured edit output to the editor as a
 * suggested removal of the table columns containing the given paragraphs —
 * the AI-sourced equivalent of the "Delete column" context-menu command in
 * suggesting mode.
 *
 * The flow:
 * 1. Resolves each anchor to its containing cell (throws when an anchor is
 *    not inside a table cell) and spans the run from the leftmost to the
 *    rightmost anchored column. A run covering every column throws — deleting
 *    the whole table is a `remove-table`.
 * 2. Marks every cell in the span with a sourced `removeTableCell` edit and
 *    strikes its content, via `deleteTableColumns` in SUGGESTING mode.
 *    Accepting removes the cells; rejecting restores them.
 * 3. Stores the reasoning on the first sourced edit of the span (the topmost
 *    marked cell). Block suggestions carry no suggestion-marker pair — the
 *    sourced edits themselves are the suggestion, resolved as one unit by
 *    groupId.
 *
 * Returns the removed cells' text (one line per row) for the chat card
 * preview.
 */
export function applyRemoveColumnsOutput(
  editor: Editor,
  options: ApplyRemoveColumnsOutputOptions
): { removedHtml: string } {
  const { authorId, groupId, locationAnchors, reasoning, source } = options;

  const resolvedAnchors = locationAnchors.map(anchor => resolveAnchorColumns(editor, anchor));
  const firstResolved = resolvedAnchors[0];
  if (!firstResolved) throw new Error('remove-columns requires at least one paragraph id');

  const [table, tablePath] = firstResolved.tableEntry;
  for (const resolved of resolvedAnchors) {
    if (!Path.equals(resolved.tableEntry[1], tablePath)) {
      throw new Error('remove-columns anchors must all identify columns of the same table');
    }
  }

  const startColumnIndex = Math.min(...resolvedAnchors.map(r => r.startColumnIndex));
  const endColumnIndex = Math.max(...resolvedAnchors.map(r => r.endColumnIndex));

  const columnCount = Queries.getTableColumnCount(table);
  if (startColumnIndex === 0 && endColumnIndex >= columnCount - 1) {
    throw new Error(
      'remove-columns would remove every column of the table — use remove-table to delete the whole table'
    );
  }

  const removedCellsByRow = getRemovedCellsByRow(table, { endColumnIndex, startColumnIndex });
  const removedHtml = serializeRemovedColumnCellsToPreviewHtml(removedCellsByRow);

  /**
   * Capture the span's corner cells before marking: the topmost cell covering
   * the run's first column and the bottommost cell covering its last column.
   * Suggesting mode only sets marks (no structural moves), so the paths stay
   * valid until the reasoning stamp below.
   */
  const firstColumnEntries = Queries.getTableCellEntriesInColumn(
    [table, tablePath],
    startColumnIndex
  );
  const lastColumnEntries = Queries.getTableCellEntriesInColumn([table, tablePath], endColumnIndex);
  const firstCellEntry = firstColumnEntries.at(0)?.tableCellEntry;
  const lastCellEntry = lastColumnEntries.at(-1)?.tableCellEntry;
  if (!firstCellEntry || !lastCellEntry) {
    throw new Error('remove-columns could not find any cells in the identified columns');
  }

  /**
   * One `deleteTableColumns` call per anchored cell: a collapsed point keeps
   * the column window unambiguous (a range across rows would widen it), and
   * repeat marks on cells covered by more than one anchor dedupe via
   * `Queries.newEdits`.
   */
  const markedCellPaths: Path[] = [];
  for (const resolved of resolvedAnchors) {
    if (markedCellPaths.some(existing => Path.equals(existing, resolved.cellPath))) continue;
    markedCellPaths.push(resolved.cellPath);

    AlineTransforms.deleteTableColumns(editor, {
      at: Editor.start(editor, resolved.cellPath),
      authorId,
      editingMode: EditingMode.SUGGESTING,
      groupId,
      source,
    });
  }

  // A sourceless removal has no sourced edit to stamp — and no AI reasoning
  // belongs on the author's own tracked changes.
  if (source) {
    stampReasoningOnFirstSourcedEdit(editor, {
      at: Editor.range(editor, firstCellEntry[1], lastCellEntry[1]),
      groupId,
      reasoning,
    });
  }

  return { removedHtml };
}

/**
 * A maximal run of adjacent (or overlapping, via merged cells) columns in one
 * table, expressed as the locationAnchors that identified them — the input
 * shape `applyRemoveColumnsOutput` takes.
 */
export interface RemoveColumnAnchorRun {
  locationAnchors: string[];
}

/**
 * Partitions a set of paragraph locationAnchors (each identifying the table
 * column span of its containing cell) into runs of adjacent columns within
 * one table. Anchors in the same column are kept together; a gap column that
 * no anchor covers splits the removal into independent runs, as does a
 * different table. An anchor that is not inside a table cell throws.
 */
export function partitionRemoveColumnAnchorsIntoRuns(
  editor: Editor,
  locationAnchors: string[]
): RemoveColumnAnchorRun[] {
  /**
   * Sort by table, then column span, then the anchor paragraph's own path so
   * the output is deterministic regardless of the order the LLM listed them.
   */
  const entries = [...new Set(locationAnchors)]
    .map(anchor => ({ anchor, ...resolveAnchorColumns(editor, anchor) }))
    .sort((a, b) => {
      const tableComparison = Path.compare(a.tableEntry[1], b.tableEntry[1]);
      if (tableComparison !== 0) return tableComparison;
      if (a.startColumnIndex !== b.startColumnIndex) return a.startColumnIndex - b.startColumnIndex;
      return Path.compare(a.paragraphPath, b.paragraphPath);
    });

  const runs: RemoveColumnAnchorRun[] = [];
  let currentTablePath: Path | null = null;
  let currentEndColumnIndex = 0;
  for (const entry of entries) {
    const isSameTable = currentTablePath && Path.equals(currentTablePath, entry.tableEntry[1]);
    const isAdjacentOrOverlapping =
      isSameTable && entry.startColumnIndex <= currentEndColumnIndex + 1;
    const currentRun = runs.at(-1);
    if (currentRun && isAdjacentOrOverlapping) {
      currentRun.locationAnchors.push(entry.anchor);
      currentEndColumnIndex = Math.max(currentEndColumnIndex, entry.endColumnIndex);
    } else {
      runs.push({ locationAnchors: [entry.anchor] });
      currentEndColumnIndex = entry.endColumnIndex;
    }
    currentTablePath = entry.tableEntry[1];
  }
  return runs;
}

interface ResolvedColumnAnchor {
  cellPath: Path;
  endColumnIndex: number;
  paragraphPath: Path;
  startColumnIndex: number;
  tableEntry: NodeEntry<TableElement>;
}

function resolveAnchorColumns(editor: Editor, locationAnchor: string): ResolvedColumnAnchor {
  const [, path] = resolveParagraphEntryByLocationAnchor(editor, locationAnchor);
  const cellEntry = Editor.above<TableCellElement>(editor, {
    at: path,
    match: isTableCellElement,
  });
  if (!cellEntry) {
    throw new Error(
      `Paragraph with locationId '${locationAnchor}' is not inside a table cell — remove-columns anchors must identify table columns`
    );
  }
  const tableEntry = Editor.above<TableElement>(editor, {
    at: cellEntry[1],
    match: isTableElement,
  });
  if (!tableEntry) {
    throw new Error(
      `Paragraph with locationId '${locationAnchor}' is inside a table cell without a table`
    );
  }
  return {
    cellPath: cellEntry[1],
    endColumnIndex: Queries.getTableCellEndColumn(editor, cellEntry),
    paragraphPath: path,
    startColumnIndex: Queries.getTableCellStartColumn(editor, cellEntry),
    tableEntry,
  };
}

/**
 * The cells the run removes, row by row, for the chat card preview. A merged
 * cell spanning several run columns appears once; rows with no cell in the
 * run's columns are skipped.
 */
function getRemovedCellsByRow(
  table: TableElement,
  options: { endColumnIndex: number; startColumnIndex: number }
): TableCellElement[][] {
  return table.children
    .map(row => {
      const cells = Queries.getTableCellsInColumnRange(row, options);
      const uniqueCells: TableCellElement[] = [];
      for (const cell of cells) {
        if (!cell) continue;
        if (uniqueCells.includes(cell)) continue;
        uniqueCells.push(cell);
      }
      return uniqueCells;
    })
    .filter(cells => cells.length > 0);
}
